import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolIcon from '@mui/icons-material/School';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useMemberspotTraining from '../../hooks/useMemberspotTraining';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function dueChip(a) {
  if (a.status === 'completed') return { label: 'Abgeschlossen', color: 'success' };
  if (a.status === 'overdue') return { label: 'Frist überschritten', color: 'error' };
  if (!a.dueAt) return { label: 'Offen', color: 'info' };
  const days = dayjs(a.dueAt).diff(dayjs(), 'day');
  if (days < 0) return { label: 'Frist überschritten', color: 'error' };
  if (days <= 3) return { label: 'Bald fällig', color: 'warning' };
  return { label: 'Offen', color: 'info' };
}

export default function MyTrainings() {
  const ms = useMemberspotTraining();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await ms.getMyAssignments();
      setAssignments(res?.assignments || []);
    } finally {
      setLoading(false);
    }
  }, [ms]);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = async assignmentId => {
    setBusyId(assignmentId);
    try {
      const res = await ms.openMyMemberspotLink(assignmentId);
      if (res?.loginUrl) window.open(res.loginUrl, '_blank', 'noopener,noreferrer');
      else toast.info('Memberspot Login-Link konnte nicht erzeugt werden.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3, maxWidth: 900, mx: 'auto' }}>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 1 }}>
        <Typography variant='h4' sx={{ fontWeight: 600 }}>
          Meine Schulungen
        </Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Hier siehst du alle deine zugewiesenen Memberspot Schulungen, deren Fristen und Fortschritt.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : assignments.length === 0 ? (
        <Alert severity='success'>Aktuell sind keine Schulungen offen. Danke fürs Dranbleiben!</Alert>
      ) : (
        <Stack spacing={2}>
          {assignments.map(a => {
            const chip = dueChip(a);
            return (
              <Paper
                key={a._id}
                sx={{
                  p: 2,
                  borderLeft: theme =>
                    `4px solid ${
                      chip.color === 'error'
                        ? theme.palette.error.main
                        : chip.color === 'warning'
                        ? theme.palette.warning.main
                        : chip.color === 'success'
                        ? theme.palette.success.main
                        : theme.palette.info.main
                    }`,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent='space-between'
                  alignItems={{ md: 'center' }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap'>
                      <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                        {a.ruleNameSnapshot}
                      </Typography>
                      <Chip label={chip.label} size='small' color={chip.color} />
                    </Stack>
                    {a.dueAt && (
                      <Typography
                        variant='body2'
                        color={chip.color === 'error' ? 'error.main' : 'text.secondary'}
                        sx={{ mt: 0.5 }}
                      >
                        Frist {dayjs(a.dueAt).format('DD.MM.YYYY')}
                        {a.status !== 'completed' &&
                          ` · ${dayjs(a.dueAt).diff(dayjs(), 'day')} Tage`}
                      </Typography>
                    )}
                    {a.status === 'overdue' && (
                      <Alert severity='warning' sx={{ mt: 1 }}>
                        Die Frist ist überschritten. Bitte schließe die Schulung zeitnah ab und stimme dich mit
                        deiner Standortleitung ab.
                      </Alert>
                    )}
                    {a.courseProgress?.length > 0 && (
                      <Box sx={{ mt: 1, maxWidth: 460 }}>
                        {a.courseProgress.map(cp => (
                          <Box key={cp.courseId} sx={{ mb: 0.5 }}>
                            <Typography variant='caption' color='text.secondary'>
                              {cp.completedPosts}/{cp.activePosts || '?'} Lektionen
                            </Typography>
                            <LinearProgress
                              variant='determinate'
                              value={
                                cp.activePosts > 0
                                  ? Math.min(100, (cp.completedPosts / cp.activePosts) * 100)
                                  : 0
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Button
                    variant='contained'
                    startIcon={<OpenInNewIcon />}
                    disabled={busyId === a._id || a.status === 'completed'}
                    onClick={() => handleOpen(a._id)}
                  >
                    Schulung öffnen
                  </Button>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
